import React from 'react';
import { SideNavLink } from '@amzn/aac-chrome-internal-api';
import { Flex, Icon } from '@amzn/storm-ui';
import { renderFlag } from 'src/commons/components/LaunchFlag/LaunchFlag.utils';
import { externalLink } from '@amzn/storm-ui-icons';
import {
  LANDING_PLACEMENT,
  LandingAppIcon,
} from 'src/commons/components/LandingAppIcon/LandingAppIcon';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';

interface SideNavLinkFeaturesProps {
  sideNavLink: SideNavLink;
  /**
   * Specifies whether the link is within the active category or is an top level nav link and is therefore focusable
   * @defaultValue `false`
   */
  isFocusable?: boolean;
}

const SideNavLinkFeatures: React.VFC<SideNavLinkFeaturesProps> = ({
  sideNavLink,
  isFocusable = false,
}) => {
  const { landingAppName, updateLandingAppName } = useConsolidatedNavContext();
  const { link, sidebarItemType, isInternal, appName, calloutTag } =
    sideNavLink;
  const isExternalLink: boolean = sidebarItemType === 'EXTERNAL_LINK';
  const isLandingPageIconDisplayed: boolean =
    !isExternalLink &&
    !isInternal &&
    (landingAppName === '' || landingAppName === appName);
  const linkHasFeatures: boolean =
    isLandingPageIconDisplayed || !!calloutTag || isExternalLink;

  if (!linkHasFeatures) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      data-ccx-e2e-id={`sidenav-link-features-${appName}`}
    >
      {!!calloutTag && renderFlag(sideNavLink)}
      {isExternalLink && <Icon type={externalLink} size="small" />}
      {isLandingPageIconDisplayed && !!updateLandingAppName && appName && (
        <LandingAppIcon
          appName={appName ?? ''}
          tooltipLabel={link.label}
          landingAppName={landingAppName ?? ''}
          landingPlacement={LANDING_PLACEMENT.CONSOLIDATED_NAV}
          updateLandingAppName={updateLandingAppName}
          tabIndex={isFocusable ? 0 : -1}
        />
      )}
    </Flex>
  );
};

export default SideNavLinkFeatures;
